import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SVG } from 'Components';
import { ROUTES, SVG_TYPE } from 'Shared/enums';
import { MainType } from './Container';

const QUESTION_TYPE = {
  basic: 'PODSTAWOWY',
  specialized: 'SPECJALISTYCZNY',
};
const mapStoredAnswerToOption = (question, storedAnswer) => {
  if (!storedAnswer) return storedAnswer;

  if (question?.type === QUESTION_TYPE.specialized) {
    if (storedAnswer === 'A') return 'answerA';
    if (storedAnswer === 'B') return 'answerB';
    if (storedAnswer === 'C') return 'answerC';
    return storedAnswer;
  }

  return storedAnswer;
};

function FastTrackExaminationPage(props: MainType) {
  const {
    actions: { getDrivingTest, getDrivingTestAnswer, finishDrivingTest, saveTask },
    drivingTest,
  } = props;
  const navigate = useNavigate();

  const [isStart, setIsStart] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isAnswerLoading, setIsAnswerLoading] = useState(false);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [answerResults, setAnswerResults] = useState<Record<number, { isAnsweredCorrectly: boolean; correctAnswer?: string; userAnswer?: string }>>(
    {},
  );
  const [maxVisibleIndex, setMaxVisibleIndex] = useState<number>(0);
  const [savedQuestions, setSavedQuestions] = useState<Record<string, boolean>>({});
  const [showSwipeNote, setShowSwipeNote] = useState<boolean>(false);

  const hintTimeoutRef = useRef<number | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef<number>(0);
  const didScrollOnEnterRef = useRef<boolean>(false);

  const questions = useMemo(() => drivingTest?.question || [], [drivingTest]);
  const totalQuestions = questions.length;
  const currentQuestion = questions[questionIndex];
  const currentQuestionResult = answerResults[questionIndex];
  const isCurrentQuestionSaved = Boolean(currentQuestion?.uuid && savedQuestions[currentQuestion.uuid]);
  const mediaPrefetchLimit = questionIndex + 2;

  const restoreExamProgress = (examQuestions) => {
    if (!examQuestions || examQuestions.length === 0) {
      return;
    }

    const restoredSelectedAnswers: Record<number, string> = {};
    const restoredAnswerResults: Record<number, { isAnsweredCorrectly: boolean; correctAnswer?: string; userAnswer?: string }> = {};

    examQuestions.forEach((question, index) => {
      if (question?.isAnswered) {
        const storedAnswer = question?.answer;
        if (storedAnswer) {
          restoredSelectedAnswers[index] = mapStoredAnswerToOption(question, storedAnswer);
        }

        const mapped = mapStoredAnswerToOption(question, storedAnswer);
        restoredAnswerResults[index] = {
          isAnsweredCorrectly: Boolean(question?.isAnsweredCorrectly),
          userAnswer: mapped,
          correctAnswer: Boolean(question?.isAnsweredCorrectly) ? mapped : undefined,
        };
      }
    });

    const firstUnansweredIndex = examQuestions.findIndex((question) => !question?.isAnswered);
    const nextQuestionIndex = firstUnansweredIndex === -1 ? examQuestions.length - 1 : firstUnansweredIndex;

    setSelectedAnswers(restoredSelectedAnswers);
    setAnswerResults(restoredAnswerResults);
    setQuestionIndex(nextQuestionIndex >= 0 ? nextQuestionIndex : 0);
    setMaxVisibleIndex(nextQuestionIndex >= 0 ? nextQuestionIndex : 0);
    setShowSwipeNote(Boolean(restoredAnswerResults[nextQuestionIndex]));
    setIsStart(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      let response = drivingTest;
      if (!drivingTest) {
        response = await getDrivingTest();
      }

      if (response?.question?.length) {
        restoreExamProgress(response.question);
        return;
      }

      navigate({ pathname: ROUTES.DRIVING_TEST_SUMMARY });
    };

    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (hintTimeoutRef.current) {
        window.clearTimeout(hintTimeoutRef.current);
      }
    };
  }, []);

  // Scroll to bottom once when exam starts
  useEffect(() => {
    if (!isStart || didScrollOnEnterRef.current) return;
    const vp = viewportRef.current;
    if (!vp) return;

    // Scroll to bottom to reveal the latest question
    try {
      vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
    } catch (e) {
      vp.scrollTop = vp.scrollHeight;
    }

    didScrollOnEnterRef.current = true;
  }, [isStart]);

  // Free/native scrolling: update active question index based on scroll position
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    let rafId: number | null = null;

    const onScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

        rafId = requestAnimationFrame(() => {
          const sections = Array.from(vp.querySelectorAll('.fast-track-exam__question')) as HTMLElement[];
          if (!sections.length) return;

          const vpRect = vp.getBoundingClientRect();
          const vpCenter = vpRect.top + vp.clientHeight / 2;

          let closestIndex = 0;
          let closestDistance = Infinity;

          sections.forEach((el, i) => {
            const rect = el.getBoundingClientRect();
            const elCenter = rect.top + rect.height / 2;
            const dist = Math.abs(elCenter - vpCenter);
            if (dist < closestDistance) {
              closestDistance = dist;
              closestIndex = i;
            }
          });

          // detect scroll direction (down = true)
          const currentScrollTop = vp.scrollTop || 0;
          const isScrollingDown = currentScrollTop > lastScrollTopRef.current;
          lastScrollTopRef.current = currentScrollTop;

          // clamp to allowed visible index
          const clampedIndex = Math.min(closestIndex, maxVisibleIndex);

          // If scrolling down, prefer advancing when either closestIndex moved forward
          // or the next section crossed a visible threshold (60% of viewport height)
          if (isScrollingDown) {
            if (showSwipeNote) {
              setShowSwipeNote(false);
            }

            const nextIdx = Math.min(questionIndex + 1, maxVisibleIndex);
            const nextEl = sections[nextIdx];
            const threshold = vp.clientHeight * 0.5;
            let shouldAdvance = clampedIndex > questionIndex;

            if (!shouldAdvance && nextEl) {
              const nextRect = nextEl.getBoundingClientRect();
              const nextTopRelative = nextRect.top - vpRect.top;
              if (nextTopRelative < threshold) {
                shouldAdvance = true;
              }
            }

            if (shouldAdvance) {
              const target = Math.min(clampedIndex, maxVisibleIndex);
              if (target > questionIndex) setQuestionIndex(target);
            }
          }
        });
    };


    vp.addEventListener('scroll', onScroll, { passive: true });

    // initialise
    onScroll();

    return () => {
      vp.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [questions, questionIndex, maxVisibleIndex, showSwipeNote]);

  const handleSetAnswer = async (answer: string) => {
    if (isAnswerLoading || !currentQuestion || answerResults[questionIndex]) {
      return;
    }

    setIsAnswerLoading(true);

    // optimistic UI: mark selected immediately so button shows active state
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));

    try {
      const response = await getDrivingTestAnswer({
        answer,
        questionUuid: currentQuestion?.uuid,
        drivingTestUuid: drivingTest?.uuid,
      });

      if (response === 422) {
        setIsAnswerLoading(false);
        navigate({ pathname: ROUTES.LOGIN_PAGE });
        return;
      }

      const mappedUserAnswer = mapStoredAnswerToOption(currentQuestion, response?.userAnswer);
      const mappedCorrectAnswer = mapStoredAnswerToOption(currentQuestion, response?.correctAnswer);

      setAnswerResults((prev) => ({
        ...prev,
        [questionIndex]: {
          isAnsweredCorrectly: Boolean(response?.isAnsweredCorrectly),
          correctAnswer: mappedCorrectAnswer,
          userAnswer: mappedUserAnswer,
        },
      }));

      setShowSwipeNote(true);

      // reveal next question after answering current
      setMaxVisibleIndex((prev) => Math.max(prev, questionIndex + 1));
    } catch (err) {
      // revert optimistic selection on error
      setSelectedAnswers((prev) => {
        const copy = { ...prev };
        delete copy[questionIndex];
        return copy;
      });
    } finally {
      setIsAnswerLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!currentQuestion?.uuid || isBookmarkLoading) {
      return;
    }

    setIsBookmarkLoading(true);

    const response = await saveTask({
      taskUuid: currentQuestion.uuid,
      isQuestionSaved: isCurrentQuestionSaved,
    });

    if (response) {
      setSavedQuestions((prev) => ({
        ...prev,
        [currentQuestion.uuid]: !isCurrentQuestionSaved,
      }));
    }

    setIsBookmarkLoading(false);
  };

  const finishExam = async () => {
    if (!drivingTest?.uuid) {
      return;
    }
    const response = await finishDrivingTest(drivingTest.uuid, { fastTrack: true });
    localStorage.setItem('EXAM_SUMMARY', JSON.stringify(response));
    navigate({ pathname: ROUTES.DRIVING_TEST_SUMMARY });
  };

  const renderQuestionMedia = (filePath, shouldLoadMedia: boolean) => {
    if (!filePath) {
      return null;
    }

    if (!shouldLoadMedia) {
      return <div className="fast-track-exam__media-placeholder" />;
    }

    const fileName = filePath.substring(filePath.lastIndexOf('/') + 1);

    if (fileName.endsWith('.mp4')) {
      return (
        <video src={filePath} controls={false} autoPlay muted playsInline preload="none">
          Twoja przeglądarka nie obsługuje elementu video.
        </video>
      );
    }

    if (fileName.endsWith('.webp')) {
      return <img className="fast-track-exam__img" src={filePath} alt="" loading="lazy" decoding="async" />;
    }

    return (
      <div className="fast-track-exam__empty-media">
        <SVG type={SVG_TYPE.NO_CAMERA} />
        Brak mediów do pytania
      </div>
    );
  };

  const getOptionState = (optionValue: string, selectedAnswer: string, result?: { isAnsweredCorrectly: boolean; correctAnswer?: string }) => {
    const isSelected = selectedAnswer === optionValue;
    const isCorrect = Boolean(result) && result?.correctAnswer === optionValue;
    const isWrongSelected = Boolean(result) && isSelected && !result?.isAnsweredCorrectly;

    return {
      isSelected,
      isCorrect,
      isWrongSelected,
      showCheckBadge: Boolean(result) && isCorrect,
      showWrongBadge: isWrongSelected,
    };
  };

  return isStart && currentQuestion ? (
    <div className="fast-track-exam">
      <div className="fast-track-exam__top">
        <div className="fast-track-exam__meta-group">
          <div className="fast-track-exam__meta">
            <p className="paragraph">Kategoria</p>
            <div className="box box--small">{drivingTest?.category}</div>
          </div>
          <div className="fast-track-exam__meta">
            <p className="paragraph">Pytania:</p>
            <div className="fast-track-exam__counter">
              {questionIndex + 1} z {totalQuestions}
            </div>
          </div>
        </div>
        <button type="button" className="fast-track-exam__close" onClick={finishExam} aria-label="Przerwij egzamin">
          <SVG type={SVG_TYPE.CLOSE_BOLD} />
        </button>
      </div>

      <div className="fast-track-exam__viewport">
        <div className="fast-track-exam__track" ref={viewportRef}>
          {questions.map((question, index) => {
            const selectedAnswer = selectedAnswers[index];
            const result = answerResults[index];
            const isBlocked = index > maxVisibleIndex;
            const shouldLoadMedia = index <= mediaPrefetchLimit;

            return (
              <section
                key={question?.uuid || index}
                className={`fast-track-exam__question ${isBlocked ? 'fast-track-exam__question--blocked' : index === questionIndex ? 'fast-track-exam__question--active' : 'fast-track-exam__question--inactive'}`}>
                <div className="fast-track-exam__question-inner">
                  <div className="fast-track-exam__photo">{renderQuestionMedia(question?.filePath, shouldLoadMedia)}</div>

                  <div className="fast-track-exam__content-card">
                    <div className="fast-track-exam__question-header">
                      <p className="fast-track-exam__question-text paragraph">{question?.question}</p>
                      <button
                        type="button"
                        className={`fast-track-exam__bookmark ${isCurrentQuestionSaved ? 'fast-track-exam__bookmark--active' : ''}`}
                        aria-label="Zapisz pytanie"
                        onClick={handleSaveQuestion}
                        disabled={isBookmarkLoading}>
                        <SVG type={SVG_TYPE.SAVE_LABEL} />
                      </button>
                    </div>

                    {question?.type === QUESTION_TYPE.basic ? (
                      <div className="fast-track-exam__answers fast-track-exam__answers--basic">
                        {[
                          { value: 'T', label: 'TAK' },
                          { value: 'N', label: 'NIE' },
                        ].map((option) => {
                          const state = getOptionState(option.value, selectedAnswer, result);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={Boolean(result) || isAnswerLoading}
                              onClick={() => handleSetAnswer(option.value)}
                              className={`fast-track-exam__basic-answer ${state.isSelected ? 'fast-track-exam__basic-answer--active' : ''} ${
                                state.isCorrect ? 'fast-track-exam__basic-answer--correct' : ''
                              } ${state.isWrongSelected ? 'fast-track-exam__basic-answer--wrong' : ''}`}>
                              <span className="fast-track-exam__answer-letter">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="fast-track-exam__answers fast-track-exam__answers--specialized">
                        {[
                          { value: 'answerA', label: 'A', text: question.answerA },
                          { value: 'answerB', label: 'B', text: question.answerB },
                          { value: 'answerC', label: 'C', text: question.answerC },
                        ].map((option) => {
                          const state = getOptionState(option.value, selectedAnswer, result);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              disabled={Boolean(result) || isAnswerLoading}
                              onClick={() => handleSetAnswer(option.value)}
                              className={`fast-track-exam__answer-row ${state.isSelected ? 'fast-track-exam__answer-row--active' : ''} ${
                                state.isCorrect ? 'fast-track-exam__answer-row--correct' : ''
                              } ${state.isWrongSelected ? 'fast-track-exam__answer-row--wrong' : ''}`}>
                              <span className="fast-track-exam__answer-letter">
                                {option.label}
                                {state.showCheckBadge && (
                                  <span className="fast-track-exam__answer-badge fast-track-exam__answer-badge--success">
                                    <SVG type={SVG_TYPE.CIRCLE_GREEN} />
                                  </span>
                                )}
                                {state.showWrongBadge && (
                                  <span className="fast-track-exam__answer-badge fast-track-exam__answer-badge--error">
                                    <SVG type={SVG_TYPE.CIRCLE_RED} />
                                  </span>
                                )}
                              </span>
                              <span className="paragraph">{option.text}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                          {currentQuestionResult && showSwipeNote && (
                            <div className="fast-track-exam__swipe-note">
                              <SVG type={SVG_TYPE.FINGER_SWIPE} className="fast-track-exam__swipe-svg" />
                              <p>Przesuń palcem żeby zobaczyć pytanie</p>
                            </div>
                          )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  ) : null;
}

export default FastTrackExaminationPage;
